import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import OtherUserGroupModel from "@/commons/models/OtherUserGroup";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * 施設関係者グループ API 操作クラス
 */
export default class OtherUserGroups {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GET OtherUserGroup
   */
  public async get(userStore: LoginUser, groupId: number): Promise<OtherUserGroupModel> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      groupId: groupId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherUserGroup, formatParams);
    const response = await this.commonService.executeGet<OtherUserGroupModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET OtherUserGroup
   */
  public async gets(userStore: LoginUser, queryParams?: { startIndex?: number; endIndex?: number; targetFacilityId?: number }): Promise<Array<OtherUserGroupModel>> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherUserGroups, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<OtherUserGroupModel>>(userStore, requestUrl, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET OtherUserGroupCount
   */
  public async getCount(userStore: LoginUser, queryParams?: { existFamily: boolean }): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherUserGroups, formatParams);
    const params: any = queryParams ? queryParams : {};
    // countの場合は末尾に「count」をつける
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * POST OtherUserGroup
   */
  public async post(userStore: LoginUser, requestBody: OtherUserGroupsPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherUserGroups, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH OtherUserGroup
   */
  public async patch(userStore: LoginUser, groupId: number, requestBody: OtherUserGroupsPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      groupId: groupId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherUserGroup, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE OtherUserGroup
   */
  public async delete(userStore: LoginUser, groupId: number): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      groupId: groupId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherUserGroup, formatParams);
    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface OtherUserGroupsPostModel {
  /** グループ名 */
  name: string;
  /** グループ種類ID */
  groupTypeId: number;
}

export interface OtherUserGroupsPatchModel {
  /** グループ名 */
  name: string;
  /** グループ種類ID */
  groupTypeId: number;
  /** 更新日時 */
  updated: number;
}
